import { useEffect, useState } from "react"
import { fetchObjectUrl } from "../../api/uploads"

/**
 * Fetch an authenticated file into an object URL, and revoke it on unmount.
 *
 * The revoke is the point. Each object URL pins the whole file in memory until
 * it is released, so a thread scrolled past a few hundred images would hold
 * every one of them — and nothing about that failure looks like a leak until the
 * tab is using two gigabytes.
 *
 * `enabled` defers the fetch, so an image far down a list is not downloaded
 * until something decides it should be.
 */
export function useObjectUrl(path, { enabled = true } = {}) {
  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  /* Bumped to ask again. A storage read that failed once is worth retrying; a
     404 is not, and `error.retryable` says which this was. */
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!path || !enabled) return undefined

    let cancelled = false
    let created = null

    setLoading(true)
    setError(null)

    fetchObjectUrl(path)
      .then((objectUrl) => {
        created = objectUrl
        // Unmounted while the fetch was in flight: revoke immediately rather
        // than handing a URL to a component that is gone.
        if (cancelled) {
          URL.revokeObjectURL(objectUrl)
          return
        }
        setUrl(objectUrl)
      })
      .catch((err) => {
        if (!cancelled) setError(Object.assign(new Error(err.message), { retryable: err.retryable }))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [path, enabled, attempt])

  return { url, error, loading, retry: () => setAttempt((n) => n + 1) }
}
